import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { AxiosError } from 'axios';
import useApiWrapper from 'helpers/hooks/useApiWrapper';
import { loadCalculation, runCalculation } from 'modules/af_tool/api';
import { LoadCalculationResponse, RunCalculationResponse } from 'modules/af_tool/api/types';
import { dashboard } from 'routes/routePaths';
import Spinner from 'shared/Spinner/Spinner';
import { Nullable } from 'utils/types';
import { State as LocalItemsState } from 'utils/store/localItemsStore/types';
import { createInitialState, reducer, runRequest } from 'utils/store/localItemsStore';

import { Form } from './partials';
import { Context, Props, Resources, TFormValues } from './CalculationId.types';
import styles from './CalculationId.module.scss';
import useBodyClass from 'helpers/hooks/useBodyClass';
import { mapContext, mapFormValues, mapInitialData, mapResources } from './utils/mapper';
import { createResultsViewModel, IResultsViewModel } from './utils';
import { Modal } from 'modules/af_cm_ui_kit/components';

const isCalculationExist = (err: AxiosError | null) => {
  return err && err.response ? err.response.status !== 404 : true;
};

const CalculcationId: React.FC<Props> = ({ match, history }) => {
  const calculationId: string = match.params.calculation_id;
  const calculationState = useApiWrapper<LoadCalculationResponse>({ runRequest: () => loadCalculation(calculationId) });
  const [runCalculationState, runCalculationStateDispatch] = useReducer<
    React.Reducer<LocalItemsState<RunCalculationResponse>, any>
  >(reducer, createInitialState({}, false));
  const [initialData, setInitialData] = useState<Nullable<LoadCalculationResponse['initialData']>>();
  const [resources, setResources] = useState<Resources>();
  const [context, setContext] = useState<Context>();
  const [results, setResults] = useState<IResultsViewModel>();
  const [formValues, setFormValues] = useState<TFormValues>();
  const [isCSVModalOpen, setIsCSVModalOpen] = useState<boolean>(false);
  useBodyClass(...styles['af_tool'].split(' '));

  if (!isCalculationExist(calculationState.axiosError)) {
    history.replace(dashboard);
  }

  useEffect(() => {
    if (calculationState.data) {
      const { context, initialData, resources } = calculationState.data;
      setInitialData(initialData);
      setFormValues(mapInitialData(initialData));
      setResources(mapResources(resources));
      setContext(mapContext(context));
      setResults(createResultsViewModel(context.results));
    }
  }, [calculationState.data]);

  useEffect(() => {
    if (runCalculationState.data && runCalculationState.loaded && !runCalculationState.axiosError) {
      const { results } = runCalculationState.data;
      setResults(createResultsViewModel(results));
    }
  }, [runCalculationState]);

  const getCalculateActionError = useCallback(() => {
    const axiosError = runCalculationState.axiosError;
    if (axiosError && axiosError.response) {
      const errorData: any = axiosError.response.data;
      const data = typeof errorData === 'object' ? errorData : { errorCode: 'PLAIN', details: 'Something went wrong.' };
      return { data };
    }

    return null;
  }, [runCalculationState.axiosError]);

  const handleSubmit = (formValues: TFormValues) => {
    runRequest<RunCalculationResponse>(runCalculationStateDispatch)(() =>
      runCalculation(calculationId, mapFormValues(formValues)),
    );
  };

  return calculationState.loaded && initialData && context ? (
    <>
      <div>
        <h1>
          {context.name}
          {context.relatedCaseNumber ? `, ${context.relatedCaseNumber}` : ''}, {calculationId}
        </h1>
        <h5>Last updated: {context.lastUpdated}</h5>
      </div>
      <div className={styles.formWrapper}>
        <Form
          onSubmit={handleSubmit}
          onExportClick={setIsCSVModalOpen.bind(null, true)}
          initialValues={formValues as TFormValues}
          resources={resources as Resources}
          results={results as IResultsViewModel}
          error={getCalculateActionError()}
        />
        <Modal isAutomated isOpen={runCalculationState.pending} title="Calculation in progress.." onClose={() => {}} />
        <Modal isOpen={isCSVModalOpen} title="Not implemented yet." onClose={setIsCSVModalOpen.bind(null, false)} />
      </div>
    </>
  ) : (
    <Spinner />
  );
};

export default CalculcationId;
